import dayjs from "dayjs";

import type {
    ILanguageContent,
    IMarquee,
    IMarqueeContentItem,
    IMarqueeDataItem,
    IScheduleMarqueeFormDataNew,
    IScheduleMarqueeItem,
    IScheduleMarqueeNew,
} from "../../types";

/**
 * 合併語言資料
 * @param existingDatas 現有語言資料
 * @param locales 語言選項
 * @returns 合併後的語言資料
 */
export const mergeLocaleDatas = (
    existingDatas: Array<{ language: string; text: string }> | null | undefined,
    locales: Array<{ value: string }>
): Array<{ language: string; text: string }> => {
    const map = new Map(existingDatas?.map((datas) => [datas.language, datas]));
    return locales.map((locale) => {
        const existing = map.get(locale.value);
        return existing || { language: locale.value, text: "" };
    });
};

/**
 * 轉換新的 API 格式 - 提交完整的 schedule_marquee
 * @param items 跑馬燈項目
 * @param locales 語言選項
 * @param nowStr 現在時間
 * @param endStr 結束時間
 * @returns 轉換後的跑馬燈項目
 */
export const convertNativeScheduleToFormItems = (
    items: IScheduleMarqueeItem[],
    locales: Array<{ value: string }>,
    nowStr: string,
    endStr: string
): IScheduleMarqueeItem[] => {
    return items.map((item) => ({
        enable_marquee: item.enable_marquee ?? false,
        color_bg: item.color_bg || "#333333f0",
        color_text: item.color_text || "#ffffffff",
        speed: String(item.speed ?? "50"),
        start_at: item.start_at ?? nowStr,
        end_at: item.end_at ?? endStr,
        datas: mergeLocaleDatas(item.datas, locales),
    }));
};

/**
 * 向下相容：載入舊的 marquee 資料
 * @param marquee 跑馬燈資料
 * @param locales 語言選項
 * @param nowStr 現在時間
 * @param endStr 結束時間
 * @returns 轉換後的跑馬燈項目
 */
export const convertLegacyMarqueeToFormItem = (
    marquee: IMarquee,
    locales: Array<{ value: string }>,
    nowStr: string,
    endStr: string
): IScheduleMarqueeItem => {
    const startDate = dayjs().format("YYYY/MM/DD HH:mm");
    const endDate = dayjs().add(99, "year").format("YYYY/MM/DD HH:mm");

    return {
        enable_marquee: marquee.enable_marquee ?? false,
        color_bg: marquee.color_bg || "#333333f0",
        color_text: marquee.color_text || "#ffffffff",
        speed: String(marquee.speed ?? "50"),
        start_at: startDate,
        end_at: endDate,
        datas: mergeLocaleDatas(marquee.datas, locales),
    };
};

/**
 * 新格式轉換函數: 舊格式 → 新格式
 * @param marquee 跑馬燈資料
 * @param locales 語言選項
 * @param nowStr 現在時間
 * @param endStr 結束時間
 * @returns 轉換後的跑馬燈項目
 */
export const convertLegacyToNewFormat = (
    marquee: IMarquee,
    locales: Array<{ value: string }>,
    nowStr: string,
    endStr: string
): IScheduleMarqueeFormDataNew => {
    const startDate = dayjs().format("YYYY/MM/DD HH:mm");
    const endDate = dayjs().add(99, "year").format("YYYY/MM/DD HH:mm");

    const content: IMarqueeContentItem[] = [{}];
    const selectedLanguages: string[] = [];

    if (marquee.datas && marquee.datas.length > 0) {
        const contentObj: Record<string, ILanguageContent> = {};
        marquee.datas.forEach((data) => {
            if (data.language !== "all") {
                contentObj[data.language] = {
                    speed: Number(marquee.speed || 50),
                    text: data.text || "",
                };
                selectedLanguages.push(data.language);
            }
        });
        content[0] = contentObj;
    }

    return {
        schedule_marquee: {
            enable_marquee: marquee.enable_marquee ?? false,
            position: "top",
            data: [
                {
                    color_bg: marquee.color_bg || "#333333f0",
                    color_text: marquee.color_text || "#ffffffff",
                    start_at: startDate,
                    end_at: endDate,
                    content: content,
                    selectedLanguages: selectedLanguages,
                },
            ],
        },
    };
};

/**
 * 新格式轉換函數: 舊 schedule_marquee[] → 新格式
 * @param items 跑馬燈項目
 * @param locales 語言選項
 * @param nowStr 現在時間
 * @param endStr 結束時間
 * @returns 轉換後的跑馬燈項目
 */
export const convertOldScheduleToNewFormat = (
    items: IScheduleMarqueeItem[],
    locales: Array<{ value: string }>,
    nowStr: string,
    endStr: string
): IScheduleMarqueeFormDataNew => {
    const dataItems: IMarqueeDataItem[] = items.map((item) => {
        // 轉換語言資料到新格式
        const content: IMarqueeContentItem[] = [{}];
        const selectedLanguages: string[] = [];

        if (item.datas && item.datas.length > 0) {
            const contentObj: Record<string, ILanguageContent> = {};
            item.datas.forEach((data) => {
                if (data.language !== "all") {
                    contentObj[data.language] = {
                        speed: Number(item.speed || 50),
                        text: data.text || "",
                    };
                    selectedLanguages.push(data.language);
                }
            });
            content[0] = contentObj;
        }

        return {
            color_bg: item.color_bg || "#333333f0",
            color_text: item.color_text || "#ffffffff",
            start_at: item.start_at || nowStr,
            end_at: item.end_at || endStr,
            content: content,
            selectedLanguages: selectedLanguages,
        };
    });

    const hasAnyEnabled = items.some((item) => item.enable_marquee);

    return {
        schedule_marquee: {
            enable_marquee: hasAnyEnabled,
            position: "top",
            data: dataItems,
        },
    };
};

/**
 * 新格式語言資料合併函數
 * @param existingContent 現有語言資料
 * @param locales 語言選項
 * @param defaultSpeed 默認速度
 * @returns 合併後的語言資料
 */
export const mergeLocaleDataForNewFormat = (
    existingContent: IMarqueeContentItem[] | undefined,
    locales: Array<{ value: string }>,
    defaultSpeed = 50
): IMarqueeContentItem[] => {
    if (!existingContent || existingContent.length === 0) {
        return [{}];
    }

    const existing = existingContent[0] || {};
    const merged: Record<string, ILanguageContent> = {};

    Object.keys(existing).forEach((langCode) => {
        if (locales.some((locale) => locale.value === langCode)) {
            merged[langCode] = existing[langCode];
        }
    });

    locales.forEach((locale) => {
        if (!merged[locale.value]) {
            merged[locale.value] = {
                speed: defaultSpeed,
                text: "",
            };
        }
    });

    return [merged];
};

/**
 * 將新格式的 schedule_marquee 轉換為舊格式的 IMarquee
 * 用於向後相容的 API 請求
 *
 * @param scheduleMarquee - 新格式的排程跑馬燈資料
 * @returns 舊格式的跑馬燈資料
 */
export const convertScheduleMarqueeToLegacy = (scheduleMarquee: IScheduleMarqueeNew | undefined): IMarquee => {
    if (!scheduleMarquee || !scheduleMarquee.enable_marquee || scheduleMarquee.data.length === 0) {
        return {
            enable_marquee: false,
            color_bg: "",
            color_text: "",
            speed: 0,
            datas: null,
        };
    }

    const firstGroup = scheduleMarquee.data[0];
    const firstContent = firstGroup.content[0] || {};

    const datas = Object.keys(firstContent)
        .filter((langCode) => {
            const langContent = firstContent[langCode];
            return langContent && langContent.text && langContent.text.trim();
        })
        .map((langCode) => ({
            language: langCode,
            text: firstContent[langCode].text.trim(),
        }));

    const speeds = Object.values(firstContent).map((content) => Number(content.speed || 50));
    const avgSpeed = speeds.length > 0 ? Math.round(speeds.reduce((a, b) => a + b, 0) / speeds.length) : 50;

    return {
        enable_marquee: true,
        color_bg: firstGroup.color_bg,
        color_text: firstGroup.color_text,
        speed: avgSpeed,
        datas: datas.length > 0 ? datas : null,
    };
};
